import { Component, DestroyRef, ElementRef, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ICoreAuthorizationDTO, ICoreMemberDTO, IDocumentDetailsDTO } from '@app/core/model';
import { PlatformService } from '@app/core/services/context/platform.service';
import { DocumentsUploadContextService } from '@app/core/services/context/factory';
import { ChooseNextAction } from '@app/products/pac/components/upload/choose-next-action.enum';
import { DialogService } from 'primeng/dynamicdialog';
import { ToastMessageOptions, MessageService } from 'primeng/api';
import { UploadError } from '@app/shared/models';
import { isFileNameValid } from '@mynexus/mynexus-ui-lib';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'mnc-documents-document-upload-select',
    templateUrl: './document-upload-select.component.html',
    styleUrls: ['./document-upload-select.component.scss'],
    standalone: false
})
export class DocumentUploadSelectComponent implements OnInit {
  @Input() multiple!: boolean;
  @Input() isDisabled!: boolean;
  @Input() accept!: string;
  @Input() maxFileSize!: number;
  @Input() maxFileNameSize!: number;
  @Output() selectFiles = new EventEmitter<File[]>();
  @Output() errorValidation = new EventEmitter<UploadError[]>();

  /* PAC */
  @Input() authorization: ICoreAuthorizationDTO;
  @Input() documents: IDocumentDetailsDTO[];
  @Input() lineData: object[];
  @Input() forceContinuedStayRequest: boolean;
  @Input() authorizationByProductId: string;
  @Input() memberDto: ICoreMemberDTO;
  @Output() createContinuedStayUpload = new EventEmitter<void>();
  @Output() levelAdjustmentUpload = new EventEmitter<void>();
  @ViewChild('inputFile', { static: true }) inputFile!: ElementRef<HTMLInputElement>;

  continuedStayMessage: ToastMessageOptions = {};
  isLoadingContinuedStay = false;

  private continueOnChange = false;
  private showContinuedStayModal = false;
  /* PAC */

  private files: File[] = [];
  private errors: UploadError[] = [];
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    public readonly sanitizer: DomSanitizer,
    private readonly contextService: PlatformService,
    private readonly documentsUploadContextService: DocumentsUploadContextService,
    private readonly dialogService: DialogService,
    private readonly messageService: MessageService,
  ) {}

  ngOnInit(): void {
     this.showContinuedStayModal =
        this.contextService.widgetFeatures.uploadContinuedStayModal
        && this.authorization.authorizationStatus === 'Active';

    if (this.showContinuedStayModal) {
      this.checkContinuedStayEligibility();
    }
  }

  onClick(): void {
    // This is a PAC specific feature. Not an ideal solution would minimize impact with this approach.
    if (this.showContinuedStayModal) {
      const ref = this.documentsUploadContextService
        .getModal(this.dialogService, this.authorization, this.lineData, this.documents, this.memberDto, this.continuedStayMessage);

      ref.onClose.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {
        switch(x) {
          case ChooseNextAction.ContinuedStay:
            this.onCreateContinuedStay();
            break;
          case ChooseNextAction.Level:
            this.levelAdjustmentUpload.emit();
            this.continueOnChange = true;
            this.inputFile.nativeElement.click();
            break;
          case ChooseNextAction.WithoutRequest:
            this.continueOnChange = true;
            this.inputFile.nativeElement.click();
            break;
          default: break;
        }
      });
    } else {
      this.inputFile.nativeElement.click();
    }
  }

  onFileSelect(event: Event): void {
    if (!this.showContinuedStayModal || this.continueOnChange) {
      this.openFileSelect(event);
    }
  }

  validate(file: File): boolean {
    // File type check
    if (this.accept && !this.isFileTypeValid(file)) {
      this.errors.push({ type: 'accept', name: file.name });
      return false;
    }

    // File size check
    if (this.maxFileSize && file.size > this.maxFileSize) {
      this.errors.push({ type: 'fileSize', name: file.name });
      return false;
    }

    // File name size check
    if (this.maxFileNameSize && file.name.length > this.maxFileNameSize) {
      this.errors.push({ type: 'fileName', name: file.name });
      return false;
    }

    // File name validation
    if (!isFileNameValid(file.name).valid) {
      this.errors.push({ type: 'fileNameInvalid', name: file.name });
      return false;
    }

    return true;
  }

  isFileTypeValid(file: File): boolean {
    const acceptableTypes = this.accept.split(',').map(type => type.trim());

    for (const type of acceptableTypes) {
      const acceptable = this.isWildcard(type) ?
        this.getTypeClass(file.type) === this.getTypeClass(type) :
        file.type === type || this.getFileExtension(file).toLowerCase() === type.toLowerCase();

      if (acceptable) {
        return true;
      }
    }

    return false;
  }

  getTypeClass(fileType: string): string {
    return fileType.substring(0, fileType.indexOf('/'));
  }

  isWildcard(fileType: string): boolean {
    return fileType.indexOf('*') !== -1;
  }

  getFileExtension(file: File): string {
    return '.' + file.name.split('.').pop();
  }

  isImage(file: File): boolean {
    return /^image\//.test(file.type);
  }

  onCreateContinuedStay() {
    if (this.continuedStayMessage.severity === 'info') {
      this.createContinuedStayUpload.emit();
    } else {
      this.messageService.add({
        severity: this.continuedStayMessage.severity,
        summary: this.continuedStayMessage.summary,
        detail: this.continuedStayMessage.detail,
        life: 5000,
      });
    }
  }

  checkContinuedStayEligibility(createContinuedStay = false):void {
    this.isLoadingContinuedStay = true;

    this.documentsUploadContextService.checkContinuedStayEligibility(this.authorizationByProductId)
      .subscribe( res => {
        this.continuedStayMessage = res;
        this.isLoadingContinuedStay = false;

        if (createContinuedStay) {
          this.onCreateContinuedStay();
        }
      });
  }

  private openFileSelect(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files: File[] = Array.from<File>(target.files as FileList);
    // Clear old errors
    this.errors = [];

    this.files = files.reduce((acc: File[], file: File) => {
      if (this.validate(file)) {
        if (this.isImage(file)) {
          // image preview
          (file as any).objectURL = this.sanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(file));
        }
        acc.push(file);
      }
      return acc;
    }, []);

    if (this.errors.length) {
      this.errorValidation.emit(this.errors);
      return;
    }

    this.selectFiles.emit(this.files);
  }
}

